import {
  AgreementRefModel,
  AgreementStatus,
  IntervalModel,
  MyPagesSpotModel,
  PriceModel,
} from "$models";
import { formatNumber } from "../utils/misc.ts";
import { formatDate } from "../utils/dateTime.ts";
import type { Spot, SpotData } from "$apis/parkito2/index.ts";

export default class SpotModel implements Spot {
  constructor(private _data: Spot) {}

  toSpotData(): SpotData {
    return {
      accessCodeSeriesId: this.accessCodeRef?.id,
      agreementPeriod: this.agreementPeriod,
      blockedFrom: this.blockedFrom,
      blockedUntil: this.blockedUntil,
      kind: this.kind,
      name: this.name,
      notes: this.notes,
      numericName: this.numericName,
      spotGroup: this.spotGroup,
    };
  }
  toMyPagesSpot(): MyPagesSpotModel {
    return new MyPagesSpotModel({
      accessCodeRef: this.accessCodeRef,
      agreementPeriod: this.agreementPeriod,
      area: this.area,
      blockedFrom: this.blockedFrom,
      blockedUntil: this.blockedUntil,
      derivedPrice: this.derivedPrice,
      id: this.id,
      kind: this.kind,
      name: this.name,
      numericName: this.numericName,
      siteId: this.siteId,
      tagIds: this.tagIds ?? [],
    });
  }

  isFree(exceptAgreements: string[] = []): boolean {
    const blockingAgreements = this.relevantAgreements.filter((a) =>
      !exceptAgreements.includes(a.id) && a.isBlockingSpot()
    );
    return blockingAgreements.length === 0;
  }

  getLastAgreementEndDate(): Date | undefined {
    const unixEndDate = this.relevantAgreements
      .map((a) => a.getEndDate())
      .filter((d) => d)
      .map((d) => d!.getTime());
    const maxTime = Math.max(...unixEndDate);
    if (maxTime === -Infinity) {
      return undefined;
    }
    return new Date(maxTime);
  }

  getFormattedPriceWithVat(): string | undefined {
    return this._data.derivedPrice?.normal
      ? formatNumber(
        this._data.derivedPrice.normal.sub +
          this._data.derivedPrice.normal.vat,
      )
      : undefined;
  }

  getFormattedPriceExVat(): string | undefined {
    return this._data.derivedPrice?.normal
      ? formatNumber(
        this._data.derivedPrice.normal.sub,
      )
      : undefined;
  }

  isResidentalVatFree(): boolean {
    return !!this._data.derivedPrice?.residental?.sub &&
      this._data.derivedPrice?.residental?.sub <
        this._data.derivedPrice?.normal?.sub;
  }

  getSortedTagNames(): string[] {
    const tagNames = this._data.tags.map((t) => t.name);
    const sortedNamesList = tagNames.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
    return sortedNamesList;
  }

  getStatusText() {
    for (const agreement of this.relevantAgreements) {
      const state = agreement.getState();
      if (state === AgreementStatus.OFFERED) {
        return "Skickat";
      } else if (state === AgreementStatus.APPROVED) {
        return `Godkänt → ${formatDate(agreement.startDate, "YYYY-MM-DD")}`;
      } else if (state === AgreementStatus.ENDING) {
        return `Aktivt → ${formatDate(agreement.endDate, "YYYY-MM-DD")}`;
      } else if (state === AgreementStatus.ACTIVE) {
        return "Aktivt";
      } else if (state === AgreementStatus.DRAFT) {
        return "Ledig (utkast)";
      }
    }
    return "Ledig";
  }

  isGroup(): boolean {
    return !!this._data.spotGroup;
  }

  get accessCodeRef() {
    return this._data.accessCodeRef;
  }
  get agreementPeriod(): IntervalModel | undefined {
    return this._data.agreementPeriod
      ? new IntervalModel(this._data.agreementPeriod)
      : undefined;
  }
  get area() {
    return this._data.area;
  }
  get belongsToGroup() {
    return this._data.belongsToGroup;
  }
  get blockedFrom() {
    return this._data.blockedFrom;
  }
  get blockedUntil() {
    return this._data.blockedUntil;
  }
  get createdAt() {
    return this._data.createdAt;
  }
  get derivedPrice() {
    if (!this._data.derivedPrice) {
      return undefined;
    }
    return {
      normal: new PriceModel(this._data.derivedPrice.normal),
      residental: new PriceModel(this._data.derivedPrice.residental),
    };
  }
  get id() {
    return this._data.id;
  }
  get kind() {
    return this._data.kind;
  }
  get name() {
    return this._data.name;
  }
  get notes() {
    return this._data.notes;
  }
  get numericName(): number {
    return this._data.numericName;
  }
  get relevantAgreements(): AgreementRefModel[] {
    return this._data.relevantAgreements.map((a) => new AgreementRefModel(a));
  }
  get siteId() {
    return this._data.siteId;
  }
  get spotGroup() {
    return this._data.spotGroup;
  }
  get tagIds() {
    return this._data.tagIds;
  }
  get tags() {
    return this._data.tags;
  }
  get updatedAt() {
    return this._data.updatedAt;
  }
}
